import { apiService } from '@/services/api-service';
import type { BoardPrepReport, ComplaintSummary, ComplaintTarget } from '@/types/models';

/**
 * Complaints against units, "reported to the board", and the board-prep report (#566).
 *
 * Its own small service rather than more methods on `DataService`: none of it is cached
 * offline or routed, so the two-layer data service would only add a pass-through.
 */
export const complaintService = {
	async getTargets(messageId: string): Promise<ComplaintTarget[]> {
		const response = await apiService.fetchApi(`/messages/${messageId}/complaint-targets`);
		return response?.data ?? [];
	},

	async addTarget(messageId: string, accountId: string): Promise<ComplaintTarget[]> {
		const response = await apiService.fetchApi(`/messages/${messageId}/complaint-targets`, {
			method: 'POST',
			body: JSON.stringify({ accountId }),
		});
		return response?.data ?? [];
	},

	async removeTarget(messageId: string, accountId: string): Promise<ComplaintTarget[]> {
		const response = await apiService.fetchApi(
			`/messages/${messageId}/complaint-targets/${encodeURIComponent(accountId)}`,
			{ method: 'DELETE' },
		);
		return response?.data ?? [];
	},

	async setReportedToBoard(messageId: string, reported: boolean): Promise<{ reportedToBoardAt: string | null }> {
		return apiService.fetchApi(`/messages/${messageId}/board-report`, {
			method: 'PUT',
			body: JSON.stringify({ reported }),
		});
	},

	async getComplaintsAgainst(accountId: string): Promise<ComplaintSummary[]> {
		const response = await apiService.fetchApi(`/complaints?againstAccountId=${encodeURIComponent(accountId)}`);
		return response?.data ?? [];
	},

	async getBoardPrep(associationId: string): Promise<BoardPrepReport> {
		return apiService.fetchApi(`/board-prep/${encodeURIComponent(associationId)}`);
	},
};
